// ─── Cluster D-main — Upload Pipeline (~610 LoC) ─────────────────────────────
// Per docs/design/app-js-split-plan-260522.md §3 Module 6.
//
// Exports: wireUploadZone, uploadFiles, retryUpload, disableUploadQuotaReached,
//          showDownsampleFeasibilityHint, showUploadStatus, hideUploadStatus.
//
// Imports from sibling split modules (Phases 1–7, already extracted):
//   globals-boot.mjs → API, sleep, authFetch
//   extract.mjs      → expandZips, extractFolder, filterByExtension, validateFile,
//                       REMEDIATE_EXTS, ALL_ACCEPTED_EXTS
//   banners.mjs      → showSectionBanner, showBothSectionBanners,
//                       showRateLimitNotification, showUploadErrors, addUploadMessage
//   upload-toast.mjs → uploadToastBegin, uploadToastComplete, uploadToastFail,
//                       uploadToastAbortRemaining, uploadToastDismiss
//   job-state.js     → jobState (Phase 10.5: class-shape retrofit, methods
//                       trackJob, getPendingRetry, setPendingRetry,
//                       removePendingRetry, saveJobIds)
//   polling.mjs      → startPolling (Phase 7 — bridge removed, direct import)
//
// Window bridges for phases not yet extracted (Phase 7 shims — remove on extraction):
//   window._adaRender                — Phase 10 (renderers.mjs) render
//   window._adaSmComputeUploadDiff   — Phase 8 (toggles.mjs) smComputeUploadDiff
//   window._adaUserContextForUpload  — Phase 8 (toggles.mjs) userContextAltTextForUpload
//   window._adaAudienceLevelForUpload — Phase 8 (toggles.mjs) audienceLevelForUpload
//   window._adaRunTokenPreflight     — Phase 9 (auth-and-billing.mjs) runTokenPreflight
//
// Globals (read-only at call time):
//   window.adatool?.log — logging
//   document           — DOM access

import { API, sleep, authFetch } from './globals-boot.mjs';
import {
    expandZips, extractFolder, filterByExtension, validateFile,
} from './extract.mjs';
import {
    showSectionBanner, showBothSectionBanners,
    showRateLimitNotification, showUploadErrors,
} from './banners.mjs';
import {
    uploadToastBegin, uploadToastComplete, uploadToastFail,
    uploadToastAbortRemaining, uploadToastDismiss,
} from './upload-toast.mjs';
import { jobState } from './job-state.js';
import { startPolling } from './polling.mjs';

// ─── Phase 7 bridge helpers ────────────────────────────────────────────────
// Called at runtime (not import-time) so boot order does not matter.
// NOTE: _startPolling() bridge from Phase 6 is REMOVED — now a direct import above.

function _render() {
    if (typeof window !== 'undefined' && typeof window._adaRender === 'function') {
        window._adaRender();
    }
}

/**
 * Re-mint the anonymous bootstrap cookie after a 401, then await it.
 *
 * authFetch awaits __adaEnsureBootstrap proactively (globals-boot.mjs), so a 401
 * means the FIRST bootstrap attempt failed (transient outage, cookie dropped, or
 * the cached promise resolved to an error). Null the cached promise so
 * __adaEnsureBootstrap re-fires a fresh POST /api/anon/bootstrap, then await it.
 * Best-effort: returns even if the re-mint fails — the caller's retry will surface
 * a second 401 as an actionable auth error rather than a generic "server error".
 *
 * Returns the bootstrap HTTP status so the caller can distinguish an ORIGIN
 * rejection (403 — spa_bootstrap_origin_ok said this URL is not our SPA) from a
 * transient failure. A 403 is NOT retry-able by reload from the same URL: the
 * user landed on a Cloud Run URL form that is not allowlisted server-side, so we
 * surface a distinct "wrong URL — use the canonical one" message rather than the
 * generic "reload and try again" auth message. LOUD-failure: a bootstrap 403 must
 * never manifest as a silent upload no-op.
 *
 * RCA: docs/design/p0-staging-web-bugs-rca-260626.md §2.5 fix #3 (401 recovery branch).
 * Staging origin-allowlist fix: docs/ops/supabase-redirect-allowlist.md §"anon-bootstrap origin".
 * @returns {Promise<number|null>} the bootstrap HTTP status, or null if unknown.
 */
async function _reBootstrap() {
    if (typeof window === 'undefined') return null;
    window.__adaBootstrapPromise = null;
    if (typeof window.__adaEnsureBootstrap === 'function') {
        try {
            const r = await window.__adaEnsureBootstrap();
            // __adaEnsureBootstrap resolves to the Response (or void on network error).
            if (r && typeof (/** @type {{status?: number}} */ (r)).status === 'number') {
                return (/** @type {{status: number}} */ (r)).status;
            }
        }
        catch (_e) { /* best-effort; retry will surface the still-401 as an auth error */ }
    }
    return null;
}

function _smComputeUploadDiff() {
    if (typeof window !== 'undefined' && typeof window._adaSmComputeUploadDiff === 'function') {
        return window._adaSmComputeUploadDiff();
    }
    return {};
}

function _userContextAltTextForUpload() {
    if (typeof window !== 'undefined' && typeof window._adaUserContextForUpload === 'function') {
        return window._adaUserContextForUpload();
    }
    return null;
}

function _audienceLevelForUpload() {
    if (typeof window !== 'undefined' && typeof window._adaAudienceLevelForUpload === 'function') {
        return window._adaAudienceLevelForUpload();
    }
    return null;
}

// M1 (font-corpus Phase 2): the embed-fonts-with-substitution knob. Returns
// true only when the checkbox is present + checked; the field is appended to
// FormData only when true (default-OFF ⇒ omit ⇒ CLI CLR-defaults false).
function _embedFontsWithSubstitutionForUpload() {
    if (typeof window !== 'undefined' && typeof window._adaEmbedFontsWithSubstitutionForUpload === 'function') {
        return window._adaEmbedFontsWithSubstitutionForUpload();
    }
    return false;
}

/**
 * @param {Array<{file: File, entry: {name: string, extract: () => Promise<File>}}>} valid
 * @returns {Promise<Array<{file: File, entry: {name: string, extract: () => Promise<File>}}> | null>}
 */
async function _runTokenPreflight(valid) {
    if (typeof window !== 'undefined' && typeof window._adaRunTokenPreflight === 'function') {
        return /** @type {Promise<Array<{file: File, entry: {name: string, extract: () => Promise<File>}}> | null>} */ (window._adaRunTokenPreflight(valid));
    }
    return valid;
}

// ─── Upload zone factory (shared by remediate + check sections) ────────────
//
// Sets up click, keydown, dragover, dragleave, drop, and file-input change
// handlers on an upload zone + hidden file input. All six handlers follow
// the same pattern; only the target elements, accepted extensions, label,
// section name, and upload callback differ.
/**
 * @param {HTMLElement} zoneEl
 * @param {HTMLInputElement} inputEl
 * @param {string[]} acceptedExts
 * @param {string} label
 * @param {string} section
 * @param {(files: Array<{name: string, extract: () => Promise<File>}>) => void} uploadFn
 */
export function wireUploadZone(zoneEl, inputEl, acceptedExts, label, section, uploadFn) {
    if (!zoneEl || !inputEl) return;

    // Zone-click opens the file picker, BUT we must skip the click if it
    // originated from a <label for="file-input"> inside the zone. The
    // browser's native label behavior already fires a trusted click on the
    // file input; a second `inputEl.click()` from this bubbled zone-click
    // triggers Chrome/Safari's double-activation guard and BOTH clicks get
    // cancelled — net effect: the picker never opens. See commit log for // noqa: comment-rot — WHY: "see commit log" is a pointer to git blame, not a stale SHA annotation
    // the reproduction. Clicks that land anywhere else in the zone (icon,
    // file-types row, hint text, empty padding) still route through the
    // programmatic `.click()` path.
    zoneEl.addEventListener('click', (e) => {
        const t = /** @type {Element | null} */ (e.target);
        if (t && typeof t.closest === 'function' && t.closest('label[for="' + inputEl.id + '"]')) return;
        inputEl.click();
    });
    zoneEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputEl.click();
        }
    });
    zoneEl.addEventListener('dragover', (e) => { e.preventDefault(); zoneEl.classList.add('dragover'); });
    zoneEl.addEventListener('dragleave', () => zoneEl.classList.remove('dragover'));
    zoneEl.addEventListener('drop', async (e) => {
        e.preventDefault();
        zoneEl.classList.remove('dragover');

        // Extract files from any dropped folders, then expand ZIPs from plain files.
        const dt = e.dataTransfer;
        const items = dt?.items ? [...dt.items] : [];
        const droppedFolder = items.some(item => {
            const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
            return entry && entry.isDirectory;
        });
        const hasZip = dt ? [...dt.files].some(f => f.name.toLowerCase().endsWith('.zip')) : false;
        if (droppedFolder || hasZip) {
            // Show processing toast while extracting leaf files.
            uploadToastBegin(0);  // total=0 triggers "Processing..." text
        }
        const folderFiles = await extractFolder(items);
        // Filter out directory entries before expandZips: browsers surface dropped
        // folders as File objects in e.dataTransfer.files (type="", size=0). We
        // already walk them via extractFolder above — passing them to expandZips too
        // would produce a spurious "unsupported file type" warning for the folder
        // itself. Use the same webkitGetAsEntry check to identify and skip directories.
        const nonFolderFiles = dt ? [...dt.files].filter((_, i) => {
            const entry = items[i]?.webkitGetAsEntry?.() ?? null;
            return !entry || !entry.isDirectory;
        }) : [];
        const plainFiles = await expandZips(nonFolderFiles, section);
        const allDropped = [...folderFiles, ...plainFiles];
        if (droppedFolder) {
            if (folderFiles.length > 0) {
                showSectionBanner(section, 'info', `Found ${folderFiles.length} supported file(s) in folder.`);
            } else {
                showSectionBanner(section, 'info', `No supported files found in folder.`);
            }
        }
        window.adatool?.log?.info('[ada-tool] dropped files:', allDropped.map(f => f.name));
        const { accepted } = filterByExtension(allDropped, acceptedExts, label, section);
        if (accepted.length > 0) {
            uploadFn(accepted);
        } else if (droppedFolder || hasZip) {
            // Dismiss "Processing..." toast — no accepted files found.
            uploadToastDismiss();
        }
    });
    inputEl.addEventListener('change', async () => {
        if (inputEl.files && inputEl.files.length > 0) {
            const allFiles = await expandZips([...inputEl.files], section);
            inputEl.value = '';
            const { accepted } = filterByExtension(allFiles, acceptedExts, label, section);
            if (accepted.length > 0) uploadFn(accepted);
        }
    });
}

// ─── Upload status banner ──────────────────────────────────────────────────
// Only shows during file upload, disappears when all files are in flight.
// Separate from system status (queue depth, processing count).

const _remediateBanner = () =>
    typeof document !== 'undefined' ? document.getElementById('remediate-banner') : null;

/** @param {string} msg @param {string} [type] */
export function showUploadStatus(msg, type = 'info') {
    const el = _remediateBanner();
    if (!el) return;
    el.className = `section-banner ${type}`;
    el.textContent = msg;
}

export function hideUploadStatus() {
    const el = _remediateBanner();
    if (el) el.className = 'section-banner hidden';
}

// ─── Quota-reached upload zone disable ────────────────────────────────────

export function disableUploadQuotaReached() {
    const zone = typeof document !== 'undefined' ? document.getElementById('upload-zone') : null;
    if (!zone) return;
    zone.style.pointerEvents = 'none';
    zone.style.opacity = '0.5';
    zone.setAttribute('aria-disabled', 'true');
    const overlay = document.createElement('div');
    overlay.className = 'quota-overlay';
    overlay.innerHTML = '<p>You have reached your quota for this billing period.</p>'
        + '<p style="font-size:13px;margin-top:8px"><a href="/account">Manage your plan</a></p>';
    zone.style.position = 'relative';
    zone.appendChild(overlay);
}

// ─── Image-bloat feasibility hint ─────────────────────────────────────────
// Pre-upload size-reduction estimate. Sets user expectations before upload.
// Related: sm_downsample_images toggle.

/** @param {Record<string, { applicable?: boolean, hi_res_image_count?: number, estimated_savings_pct?: number }> | null | undefined} feasibility */
export function showDownsampleFeasibilityHint(feasibility) {
    if (!feasibility || typeof feasibility !== 'object') return;
    let totalHiRes = 0;
    let bestSavings = 0;
    for (const jobId of Object.keys(feasibility)) {
        const f = feasibility[jobId] || {};
        if (f.applicable && (f.hi_res_image_count || 0) > 0) {
            totalHiRes += f.hi_res_image_count || 0;
            if ((f.estimated_savings_pct || 0) > bestSavings) {
                bestSavings = f.estimated_savings_pct || 0;
            }
        }
    }
    if (totalHiRes === 0) return;
    // Only nudge if the user hasn't already enabled the toggle.
    const toggle = typeof document !== 'undefined' ? /** @type {HTMLInputElement|null} */ (document.getElementById('sm-downsample-images')) : null;
    if (toggle && toggle.checked) return;
    const hintEl = typeof document !== 'undefined' ? document.getElementById('sm-downsample-images-hint') : null;
    if (hintEl) {
        const pct = bestSavings > 0 ? ` (~${bestSavings}% smaller)` : '';
        hintEl.textContent =
            `This file contains ${totalHiRes} large image(s). ` +
            `Enabling this typically reduces file size${pct} at no visible quality loss for screen viewing.`;
        hintEl.style.display = '';
    }
}

// ─── Retry pending upload (429 back-off) ──────────────────────────────────
// Retry a pending upload that was 429'd. On success: promote via jobState.
// On 429 again: keep retrying (timer continues). On other error: give up.

/** @param {import('./job-state.js').PendingRetryId} pendingId */
export async function retryUpload(pendingId) {
    const pending = jobState.getPendingRetry(pendingId);
    if (!pending || !pending.extractFn) return;
    try {
        const f = await pending.extractFn();
        const form = new FormData();
        form.append('files', f);
        // Per-leaf structural-mods (locked schema 260507): send a diff
        // against the user's stored default. Empty diff = on default.
        try { form.append('sm_prefs_diff', JSON.stringify(_smComputeUploadDiff())); }
        catch (e) { /* best-effort; sm rail may not be initialized */ }
        // Per-upload audience context for alt-text generation (260510).
        try {
            const uctx = _userContextAltTextForUpload();
            if (uctx) form.append('user_context_alt_text', uctx);
        } catch (e) { /* best-effort; user-context UI may not be initialized */ }
        // Audience-level dropdown (260510). Append only when non-Off.
        try {
            const aud = _audienceLevelForUpload();
            if (aud) form.append('audience_level', aud);
        } catch (e) { /* best-effort; audience-level UI may not be initialized */ }
        // Embed-fonts-with-substitution knob (M1). Append only when ON;
        // absence ⇒ the CLI CLR-defaults embedFontsWithSubstitution=false.
        try {
            if (_embedFontsWithSubstitutionForUpload()) form.append('embed_fonts_with_substitution', '1');
        } catch (e) { /* best-effort; toggle may not be initialized */ }
        const resp = await authFetch(`${API}/api/upload`, { method: 'POST', body: form });
        if (resp.ok) {
            const data = await resp.json();
            clearInterval(pending.retryTimer);
            jobState.removePendingRetry(pendingId);
            for (const job of data.jobs) {
                window.adatool?.log?.info('[ada-tool] pending retry succeeded:', job.id, job.filename);
                jobState.trackJob(job);
            }
            jobState.saveJobIds(); _render(); startPolling();
        } else if (resp.status === 429) {
            window.adatool?.log?.info('[ada-tool] retry still 429:', pending.name);
            // Keep timer running.
        } else {
            // Non-retryable error — give up.
            const data = await resp.json().catch(() => ({}));
            const detail = data.detail || '';
            const reason = (typeof detail === 'string' ? detail : (detail.message || '')) || `Error ${resp.status}`;
            window.adatool?.log?.error('retry failed permanently:', pending.name, reason);
            clearInterval(pending.retryTimer);
            jobState.removePendingRetry(pendingId);
            _render();
        }
    } catch (e) {
        window.adatool?.log?.error('retry error:', pending.name, e instanceof Error ? e.message : String(e));
    }
}

// ─── Main upload orchestrator ──────────────────────────────────────────────

/** @param {Array<{name: string, extract: () => Promise<File>}>} entries */
export async function uploadFiles(entries) {
    // entries are lazy extractors: {name, extract: () => Promise<File>}.
    // Extract and validate each file — skip invalid ones, continue with the rest.
    const valid = [];
    const errors = [];
    for (const entry of entries) {
        const f = await entry.extract();
        const err = await validateFile(f);
        if (err) {
            errors.push(err);
        } else {
            valid.push({ file: f, entry });
        }
    }
    if (errors.length > 0) {
        showSectionBanner('remediate', 'warning', errors.join(' '));
    }
    if (valid.length === 0) {
        if (errors.length === 0) showSectionBanner('remediate', 'error', 'No valid files selected.');
        return;
    }

    // Token preflight (260509). Counts pages locally and shows a "buy more"
    // modal if any file would exceed the user's per-format balance.
    // Institutional-unlimited users skip the preflight entirely. The
    // server-side check is still authoritative; this is just UX.
    //
    // Best-effort: the preflight is a soft UX check, NOT a gate. If it throws
    // for ANY reason (CSP-blocked lazy import, module-eval error, page-count
    // parse failure), we must NOT abort the upload — the server-side token check
    // is authoritative. A prior CSP `new Function` eval-throw escaped the
    // preflight's own catch and rejected here, silently aborting EVERY anonymous
    // upload (anon-upload-preflight-csp-eval-rca-260628). Degrade to "proceed
    // with all valid files" on any throw.
    let preflighted;
    try {
        preflighted = await _runTokenPreflight(valid);
    } catch (e) {
        window.adatool?.log?.warn?.('[ada-tool] token preflight failed; proceeding (server is authoritative):',
            e instanceof Error ? e.message : String(e));
        preflighted = valid;  // snapshot below handles the aliasing safely
    }
    if (preflighted === null) return;  // user cancelled OR redirected to Stripe
    // Snapshot FIRST — preflighted may BE the same `valid` array reference
    // (runTokenPreflight returns `valid` unchanged when the lazy page-count
    // module fails to load: auth-and-billing.ts:954). Clearing valid before
    // copying would empty preflighted too and drop every file → silent
    // zero-file upload. anon-upload-preflight-csp-eval-rca-260628.
    const survivors = [...preflighted];
    valid.length = 0;
    valid.push(...survivors);
    if (valid.length === 0) return;

    try {
        const q = await fetch(`${API}/api/queue`).then(r => r.json());
        window.adatool?.log?.info('[ada-tool] queue status:', q);
        // No backpressure — system always accepts uploads.
    } catch (e) {
        window.adatool?.log?.error('queue check failed:', e instanceof Error ? e.message : String(e));
        showBothSectionBanners('error', 'Cannot reach server. Is it running?');
        return;
    }

    window.adatool?.log?.info('[ada-tool] uploading', valid.length, 'file(s):', valid.map(v => v.file.name).join(', '));
    showUploadStatus(`Uploading ${valid.length} file(s)...`);
    uploadToastBegin(valid.length);

    const CHUNK_SIZE = 2 * 1024 * 1024;          // 2 MB chunks
    const CHUNK_THRESHOLD = 30 * 1024 * 1024;    // Use chunked upload for files >30 MB

    let uploaded = 0;
    /** @type {{ size: string[], pages: string[], quota: boolean, anonUnavailable: boolean, tokenGate: string[], format: string[], server: string[], auth: string[], bootstrapOrigin: string[] }} */
    const uploadErrors = {
        size: [],      // files exceeding size cap
        pages: [],     // files exceeding page/slide limit
        quota: false,  // quota exceeded (one message, not per-file)
        // 402 carrying the S-P2 fail-closed reason ("anonymous service
        // temporarily unavailable"): a TRANSIENT server-side anon-grant
        // failure, NOT a real credit exhaustion. Surfaced as a distinct,
        // actionable message so the fail-closed condition is OBVIOUS to the
        // user (not buried in the generic "quota exceeded" bucket, which would
        // also wrongly imply the user is out of credits and permanently
        // disable the drop target). See
        // docs/design/anon-grant-failclosed-rca-260626.md §1.
        anonUnavailable: false,
        tokenGate: [], // token-gate / quota-exhausted rejections
        format: [],    // unsupported file types / skipped
        server: [],    // server errors (500, etc.)
        auth: [],      // 401 — anonymous session not established (bootstrap cookie missing after re-mint+retry)
        bootstrapOrigin: [], // 403 from /api/anon/bootstrap — this URL form is not allowlisted server-side (wrong Cloud Run URL)
    };

    // WHY: for-of with entries() gives correctly-typed item (no T|undefined from noUncheckedIndexedAccess)
    for (const [fi, item] of valid.entries()) {
        const f = item.file;
        const entry = item.entry;
        if (valid.length > 1) {
            showUploadStatus(`Uploading ${fi + 1} of ${valid.length}: ${f.name}`);
        }
        try {
            if (f.size > CHUNK_THRESHOLD) {
                // Chunked upload for large files.
                window.adatool?.log?.info(`[ada-tool] chunked upload: ${f.name} (${(f.size / (1024 * 1024)).toFixed(0)} MB)`);

                // 1. Init
                const initForm = new FormData();
                initForm.append('filename', f.name);
                const initResp = await authFetch(`${API}/api/upload/init`, { method: 'POST', body: initForm });
                if (!initResp.ok) {
                    await initResp.json().catch(() => ({}));
                    uploadErrors.server.push(f.name);
                    uploadToastFail();
                    continue;
                }
                const { upload_id, job_id } = await initResp.json();
                jobState.trackJob({
                    id: job_id, filename: f.name, size: f.size,
                    status: 'queued', stage: 'uploading',
                });
                jobState.saveJobIds(); _render(); startPolling();

                // 2. Send chunks
                let offset = 0;
                let failed = false;
                while (offset < f.size) {
                    const end = Math.min(offset + CHUNK_SIZE, f.size);
                    const blob = f.slice(offset, end);
                    const chunkForm = new FormData();
                    chunkForm.append('chunk', blob, 'chunk.bin');
                    const chunkResp = await authFetch(`${API}/api/upload/${upload_id}/chunk`, { method: 'POST', body: chunkForm });
                    if (!chunkResp.ok) {
                        uploadErrors.server.push(f.name);
                        failed = true;
                        uploadToastFail();
                        break;
                    }
                    offset = end;
                    const pct = Math.round((offset / f.size) * 100);
                    showUploadStatus(`Uploading: ${pct}% complete`);
                    window.adatool?.log?.info(`[ada-tool] chunk ${Math.ceil(offset / CHUNK_SIZE)}/${Math.ceil(f.size / CHUNK_SIZE)}: ${f.name}`);
                }
                if (failed) continue;

                // 3. Complete
                const completeResp = await authFetch(`${API}/api/upload/${upload_id}/complete`, { method: 'POST' });
                if (!completeResp.ok) {
                    uploadErrors.server.push(f.name);
                    uploadToastFail();
                    continue;
                }
                uploaded++;
                uploadToastComplete();
            } else {
                // Standard single-request upload.
                const form = new FormData();
                form.append('files', f);
                // Per-leaf structural-mods (locked schema 260507): send a diff
                // against the user's stored default.
                try { form.append('sm_prefs_diff', JSON.stringify(_smComputeUploadDiff())); }
                catch (e) { /* best-effort; sm rail may not be initialized */ }
                // Per-upload audience context for alt-text generation (260510).
                try {
                    const uctx = _userContextAltTextForUpload();
                    if (uctx) form.append('user_context_alt_text', uctx);
                } catch (e) { /* best-effort; user-context UI may not be initialized */ }
                // Audience-level dropdown (260510). Append only when non-Off.
                try {
                    const aud = _audienceLevelForUpload();
                    if (aud) form.append('audience_level', aud);
                } catch (e) { /* best-effort; audience-level UI may not be initialized */ }
                let resp = await authFetch(`${API}/api/upload`, { method: 'POST', body: form });
                // 401-recovery (RC-B1): the anonymous-upload gate (branch 3) rejected the
                // request because the ada_spa_bootstrap cookie was absent. authFetch awaits
                // bootstrap proactively, so a 401 means the first bootstrap attempt failed —
                // re-mint the cookie and retry the upload ONCE before surfacing an error.
                if (resp.status === 401) {
                    window.adatool?.log?.info('[ada-tool] upload 401 — re-minting bootstrap + retrying once:', f.name);
                    const bootstrapStatus = await _reBootstrap();
                    if (bootstrapStatus === 403) {
                        // ORIGIN rejection: spa_bootstrap_origin_ok said this URL is not
                        // our SPA (e.g. a non-allowlisted Cloud Run URL form). Reloading
                        // the SAME URL will 403 again — surface a distinct, actionable
                        // "wrong URL" message so the user isn't stuck in a silent no-op.
                        window.adatool?.log?.error('bootstrap 403 (origin not allowlisted for this URL):', f.name);
                        uploadErrors.bootstrapOrigin.push(f.name);
                        uploadToastFail();
                        continue;
                    }
                    resp = await authFetch(`${API}/api/upload`, { method: 'POST', body: form });
                    if (resp.status === 401) {
                        // Still 401 after re-mint+retry — surface an actionable auth error,
                        // NOT the generic "server error".
                        window.adatool?.log?.error('upload still 401 after re-bootstrap:', f.name);
                        uploadErrors.auth.push(f.name);
                        uploadToastFail();
                        continue;
                    }
                }
                if (!resp.ok) {
                    const data = await resp.json().catch(() => ({}));
                    window.adatool?.log?.error('upload failed:', f.name, resp.status, data.detail || '');
                    const detail = data.detail || {};
                    // 429: server emits this only on "Free limit reached" today (anonymous monthly cap).
                    if (resp.status === 429) {
                        const msg = (typeof detail === 'string' ? detail : detail.message) || 'Rate limit reached.';
                        const retryable = (typeof detail === 'object' && detail !== null && detail.retryable === true);
                        if (retryable) {
                            // Server explicitly signaled a transient 429 — silent retry.
                            const pendingId = /** @type {import('./job-state.js').PendingRetryId} */ (`pending-${Date.now()}-${fi}`);
                            jobState.setPendingRetry(pendingId, {
                                name: f.name,
                                extractFn: entry.extract,
                                retryTimer: setInterval(() => retryUpload(pendingId), 20000),
                            });
                            window.adatool?.log?.info('[ada-tool] queued for retry:', f.name, pendingId);
                            uploadToastFail();
                            _render();
                            continue;
                        }
                        // Default: show banner, stop uploading remaining files.
                        uploadToastAbortRemaining(valid.length - fi);
                        showRateLimitNotification(msg);
                        break;
                    }
                    // 402: insufficient_credits. Two distinct sub-cases on the
                    // anonymous path — distinguished by the server reason string
                    // (data.detail.detail). "anonymous service temporarily
                    // unavailable" is the S-P2 fail-closed signal (the anon
                    // credit-grant op failed server-side — transient/infra, NOT a
                    // real quota exhaustion). Anything else is a genuine
                    // credit/quota exhaustion. We MUST surface the fail-closed
                    // case as its own visible, actionable message — "fail-closed
                    // SILENT is worse than fail-closed". See
                    // docs/design/anon-grant-failclosed-rca-260626.md.
                    if (resp.status === 402) {
                        const reason = (typeof detail === 'string'
                            ? detail
                            : (detail && typeof detail.detail === 'string' ? detail.detail : '')) || '';
                        if (/temporarily unavailable/i.test(reason)) {
                            // Transient anon-grant fail-closed: do NOT disable the
                            // drop target (it is not a permanent out-of-credits
                            // state) and do NOT bucket it as generic quota.
                            uploadErrors.anonUnavailable = true;
                        } else {
                            // Genuine quota/credit exhaustion.
                            disableUploadQuotaReached();
                            uploadErrors.quota = true;
                        }
                        uploadToastAbortRemaining(valid.length - fi);
                        break;
                    }
                    // 413: file too large
                    if (resp.status === 413) {
                        uploadErrors.size.push(f.name);
                        uploadToastFail();
                        continue;
                    }
                    // Anything else: server error
                    uploadErrors.server.push(f.name);
                    uploadToastFail();
                    continue;
                }
                const data = await resp.json();
                for (const job of data.jobs) {
                    window.adatool?.log?.info('[ada-tool] job created:', job.id, job.filename, job.status);
                    jobState.trackJob(job);
                }
                if (data.skipped) {
                    for (const s of data.skipped) {
                        if (/token.gate|quota.exhausted|please retry/i.test(s)) {
                            uploadErrors.tokenGate.push(s);
                        } else {
                            uploadErrors.format.push(s);
                        }
                    }
                }
                if (data.feasibility) {
                    showDownsampleFeasibilityHint(data.feasibility);
                }
                uploaded++;
                uploadToastComplete();
            }
            jobState.saveJobIds();
            _render();
            startPolling();

            // Throttle: max 3 uploads per second (wait 334ms between each).
            if (uploaded < valid.length) await sleep(334);
        } catch (e) {
            window.adatool?.log?.error('upload error:', f.name, e instanceof Error ? e.message : String(e));
            uploadErrors.server.push(f.name);
            uploadToastFail();
        }
    }

    const hasErrors = uploadErrors.size.length > 0 || uploadErrors.pages.length > 0
        || uploadErrors.quota || uploadErrors.anonUnavailable || uploadErrors.tokenGate.length > 0
        || uploadErrors.format.length > 0 || uploadErrors.server.length > 0
        || uploadErrors.auth.length > 0 || uploadErrors.bootstrapOrigin.length > 0;
    if (hasErrors) {
        showUploadErrors(uploadErrors);
    }
    hideUploadStatus();
}

// Re-export the jobState singleton so seam tests can reach the exact instance
// that retryUpload/uploadFiles read (module-identity safety when tsx remaps
// .js → .ts imports from TypeScript test files but NOT from .mjs source files).
export { jobState };
